import { Banner, BlockStack, Layout, Text } from '@shopify/polaris';
import { useConvertTrial } from '../hooks/useConvertTrial';
import { useLanguage } from '../providers/LanguageProvider';
import type { UsageStats } from '../types';
import { PlanTier } from '../types';

/**
 * Trial try-ons left when we start warning. Mirrors TRIAL_LOW_BALANCE_THRESHOLD
 * in the backend notifier, and the same number on the WooCommerce side: the
 * banner and the email must not disagree about when the trial is running low.
 */
export const TRIAL_LOW_BALANCE_THRESHOLD = 10;

/** "GROWTH" reads as "Growth", which is how the plan is named everywhere else. */
function planLabel(planTier: string): string {
  return planTier.charAt(0).toUpperCase() + planTier.slice(1).toLowerCase();
}

interface TrialQuotaBannerProps {
  usage: UsageStats | null;
}

/**
 * The trial's last stretch, and the one-click way out of it.
 *
 * Renders nothing until the trial is nearly spent, so a merchant who is happily
 * inside it is never nagged. This is the merchant-facing half of the storefront
 * pause: shoppers are told nothing about plans or quotas, so this banner and the
 * two emails are the only places the story is told.
 *
 * Owns its own Layout.Section, like ReviewPromptBanner, so a page that has
 * nothing to say gets no empty section.
 */
export default function TrialQuotaBanner({ usage }: TrialQuotaBannerProps) {
  const { t, language } = useLanguage();
  const { convertTrial, converting, failed } = useConvertTrial();

  if (!usage?.isTrial || usage.trialCap === null) return null;
  // The free tier has no trial and nothing to activate.
  if (usage.planTier === PlanTier.FREE) return null;

  const remaining = Math.max(0, usage.trialTryOnsRemaining ?? 0);
  if (remaining > TRIAL_LOW_BALANCE_THRESHOLD) return null;

  const exhausted = remaining <= 0;
  // While the trial runs, the billing period ends where the trial does.
  const trialEndsOn = new Date(usage.billingPeriodEnd).toLocaleDateString(
    language === 'it' ? 'it-IT' : 'en-US'
  );

  return (
    <Layout.Section>
      <Banner
        tone={exhausted ? 'critical' : 'warning'}
        title={exhausted ? t.trial.exhaustedTitle : t.trial.lowTitle}
        action={{
          content: t.trial.startPlan(planLabel(usage.planTier)),
          onAction: convertTrial,
          loading: converting,
        }}
      >
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            {exhausted
              ? t.trial.exhaustedBody(usage.trialCap, trialEndsOn)
              : t.trial.lowBody(remaining, usage.trialCap)}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {t.trial.approvalNote}
          </Text>
          {failed && (
            <Text as="p" variant="bodySm" tone="critical">
              {t.trial.failed}
            </Text>
          )}
        </BlockStack>
      </Banner>
    </Layout.Section>
  );
}
